import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'

// Base schema for revenue recognition form data
const baseRevenueRecognitionFormSchema = z.object({
  // Map of period date to recognition data (e.g., { "2024-01-01": { amount: 5000.00, status: false } })
  // Period date represents first day of month, matching DB date format
  // Using object structure for future extensibility
  periods: z.record(
    z.string(),
    z.object({
      amount: z.number().nullable(),
      isLocked: z.boolean().default(false), // false = unlocked, true = locked
    })
  ).default({}),
})

/**
 * Function to create schema with conditional validation based on total contract value
 */
export const createRevenueRecognitionFormSchema = (totalContractValue?: number) => {
  return baseRevenueRecognitionFormSchema.superRefine((data, ctx) => {
    // Validate each period's amount
    Object.entries(data.periods).forEach(([periodId, periodData]) => {
      if (periodData.amount === null || periodData.amount === undefined) {
        zodValidationHelpers.minNumber(ctx, ['periods', periodId, 'amount'], 0, 'Field is required')
      } else if (periodData.amount < 0) {
        zodValidationHelpers.minNumber(ctx, ['periods', periodId, 'amount'], 0, 'Field cannot be negative')
      }
    })

    // Validate total of all periods equals total contract value
    if (totalContractValue !== null && totalContractValue !== undefined) {
      const totalRecognized = Object.values(data.periods).reduce(
        (sum, period) => sum + (period.amount || 0),
        0
      )
      
      if (Math.abs(totalRecognized - totalContractValue) > 0.01) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Total revenue recognition must equal total contract value of ${totalContractValue}`,
          path: ['periods'],
        })
      }
    }
  })
}

// Schema for the revenue recognition form with conditional validation
export const revenueRecognitionFormSchema = createRevenueRecognitionFormSchema()

export type RevenueRecognitionFormData = Expand<z.infer<typeof revenueRecognitionFormSchema>>

